import { Seo } from "../components/Seo";
import { CtaBand, Eyebrow } from "../components/Blocks";
import { Link, returnToPreviousPage, useNavigate } from "../components/Layout";

export default function GuideFirstClassicAge() {
  const navigate = useNavigate();

  return <main>
    <Seo path="/guide/first-classic-age" />
    <section className="legal guide-page">
      <nav className="guide-breadcrumb" aria-label="현재 위치">
        <Link href="/group-visit" navigate={navigate} onClick={(event) => returnToPreviousPage(event, "/group-visit")}>어린이집·유치원 단체관람</Link>
        <span aria-hidden="true">/</span>
        <span aria-current="page">관람 연령 안내</span>
      </nav>
      <Eyebrow>GUIDE</Eyebrow>
      <h1>영유아 클래식 공연,<br />몇 살부터 괜찮을까</h1>
      <p>먼저 결론부터 말씀드리면, <b>어린이집이나 유치원에 다니는 아이라면 모두 관람할 수 있습니다.</b> 마이리틀클래식은 연령으로 관람을 제한하지 않고, 아이들의 연령 구성에 맞춰 진행 방식과 프로그램을 조정합니다.</p>
      <p>그래서 실제로 중요한 질문은 "볼 수 있느냐"가 아니라 <b>"이 나이의 아이에게 무엇을 기대하면 좋으냐"</b>입니다. 같은 만 3세라도 앉아 있을 수 있는 시간, 이야기를 따라가는 방식, 소리에 반응하는 방식이 다릅니다. 연령대별로 아이가 공연장에서 실제로 무엇을 하는지 정리했습니다.</p>

      <article className="guide-content">
        <section>
          <h2>돌 전후 ~ 만 2세 · 소리에 몸이 먼저 반응하는 시기</h2>
          <p>이 시기 아이는 이야기의 줄거리를 따라가지 않습니다. 대신 소리의 크기와 높낮이, 리듬의 변화에 몸으로 반응합니다. 현악기의 낮은 울림에 가만히 있다가 빠른 구간에서 손을 흔드는 식입니다.</p>
          <p>한자리에 앉아 있는 시간은 짧습니다. 일어서고, 앞으로 나오고, 중간에 나갔다 들어오는 것이 자연스러운 연령입니다. 보호자가 함께하는 가족 단위 관람이 적합하며, 기관 단체관람이라면 인솔 인원을 넉넉히 잡는 편이 안전합니다.</p>
          <p><b>기대할 것</b> — 처음 듣는 실제 악기 소리에 대한 반응. 그 이상은 이 시기에 무리입니다.</p>
        </section>

        <section>
          <h2>만 3세 · 이야기가 들어오기 시작하는 시기</h2>
          <p>줄거리를 따라가기 시작합니다. "다음에 어떻게 될까"를 궁금해하고, 삽화를 보며 장면을 기억합니다. 다만 집중은 이어지지 않고 파도처럼 오가는 경우가 많습니다. 잠시 몰입했다가 흐트러지고, 새로운 자극이 들어오면 다시 관심을 보이는 식입니다.</p>
          <p>그래서 이 연령의 프로그램은 한 흐름으로 길게 가는 것보다, 이야기·질문·움직임·연주가 번갈아 나오는 구성이 맞습니다. 아이가 중간에 흐트러지는 것은 프로그램이 실패해서가 아니라 이 나이의 정상적인 집중 패턴입니다.</p>
          <p><b>기대할 것</b> — 이야기의 큰 줄기를 기억하고 돌아오는 것. 곡명이나 악기 이름은 아직 아닙니다.</p>
        </section>

        <section>
          <h2>만 4~5세 · 참여가 가능해지는 시기</h2>
          <p>가장 많은 것이 가능해지는 구간입니다. 인물의 감정을 이해하고, 슬픈 장면에 어울리는 음악과 신나는 장면에 어울리는 음악이 다르다는 것을 스스로 알아챕니다. 진행자의 질문에 손을 들어 답하고, 악기를 구분해 이름을 부르기도 합니다.</p>
          <p>이야기와 참여 요소가 번갈아 이어지는 40분 안팎의 프로그램에도 비교적 안정적으로 참여하는 경우가 많으며, 공연이 끝난 뒤 악기를 직접 만나는 시간에 적극적으로 반응하기도 합니다.</p>
          <p><b>기대할 것</b> — 공연 내용을 원에 돌아가 이야기로 옮기는 것. 실제로 이 시기 아이들이 그날 들은 곡을 흥얼거리는 경우가 많습니다.</p>
        </section>

        <section>
          <h2>혼합 연령이라면 어린 쪽에 맞춥니다</h2>
          <p>어린이집·유치원 단체관람은 대개 연령이 섞입니다. 이때 기준을 큰 아이에게 맞추면 어린 아이가 견디는 시간이 되고, 어린 쪽에 맞추면 큰 아이는 조금 쉽게 느끼지만 전체가 편안합니다.</p>
          <p>결론적으로 <b>가장 어린 연령을 기준으로 길이와 난이도를 잡고, 큰 아이에게는 참여할 역할을 주는 방식</b>이 현장에서 가장 안정적입니다.</p>
        </section>

        <section>
          <h2>"우리 아이가 조용히 못 앉아 있어서요"</h2>
          <p>공연을 미루는 가장 흔한 이유지만, 사실 이건 아이의 문제가 아니라 공연장의 문제인 경우가 많습니다. 무대가 높고 객석이 멀면 아이는 볼 것이 없어서 움직입니다.</p>
          <p>마이리틀클래식은 아이의 움직임과 목소리를 공연을 방해하는 소리가 아니라 음악과 처음 만나는 방식으로 봅니다. 무대를 낮추고 객석을 가까이 붙인 것도, 진행자가 아이의 반응을 무대로 연결하는 것도 같은 이유입니다. 자세한 기준은 <Link className="inline-link" href="/about" navigate={navigate}>마이리틀클래식 소개</Link>에 정리해 두었습니다.</p>
        </section>

        <section>
          <h2>정리하면</h2>
          <p>돌 전후에는 소리에 대한 반응을, 만 3세에는 이야기의 줄기를, 만 4~5세에는 참여와 기억을 기대하시면 됩니다. 나이가 어려서 이르다기보다, 그 나이에 맞는 기대를 가지고 가면 어느 연령이든 첫 클래식이 될 수 있습니다.</p>
          <p>다시 말씀드리면 <b>어린이집·유치원에 다니는 아이들은 모두 관람 대상</b>입니다. 방문 인원과 연령 구성을 알려주시면 그에 맞는 진행 방식을 함께 정합니다. <Link className="inline-link" href="/venue" navigate={navigate}>동탄 공연장</Link>은 좌석이 따로 없는 좌식 공연장이라 아이들이 편하게 앉아 관람하며, 공연 45분과 악기 체험 15분 내외로 운영합니다. 회차별 관람 기록은 <Link className="inline-link" href="/group-visit" navigate={navigate} onClick={(event) => returnToPreviousPage(event, "/group-visit")}>단체관람 페이지</Link>에서 보실 수 있습니다.</p>
          <p>연령대별 설명은 발달 단계에 따른 일반적인 안내이며, 아이마다 차이가 있습니다.</p>
        </section>
      </article>

      <nav className="guide-return" aria-label="단체관람 안내로 돌아가기">
        <div><span>GROUP VISIT</span><strong>어린이집·유치원 단체관람 안내</strong></div>
        <Link className="button ghost" href="/group-visit" navigate={navigate} onClick={(event) => returnToPreviousPage(event, "/group-visit")}>단체관람 안내로 돌아가기 <span aria-hidden="true">→</span></Link>
      </nav>
    </section>

    <CtaBand
      title="아이들의 연령을 알려주세요"
      body="방문 인원과 연령대를 알려주시면 그 구성에 가장 적합한 프로그램과 진행 방식을 제안합니다."
      type="visit"
    />
  </main>;
}
